import React, { useEffect, useRef, useState } from "react";
import { Button, Tooltip } from "antd";
import styles from "./adPrice.scss";
import { displayPrice } from "../../../../../services";
import { AdStatus, LocalStorageKey } from "../../../../../models";
import ReactGA from "react-ga4";
import { getIconUrl } from "../../../../../services/asset";

const MAX_PRICE_DIGITS = 9;
const numericOnly = (val: string) => {
  if (val === "") return true;
  if (!/^\d+([.,]?\d*)$/.test(val)) return false;
  const integerPart = val.split(/[.,]/)[0];
  return integerPart.length <= MAX_PRICE_DIGITS;
};

const PriceInput = ({ value, onChange, onKeyDown, autoFocus, small }: any) => (
  <input
    autoFocus={autoFocus}
    type="text"
    inputMode="decimal"
    className={small ? styles.priceInputSmall : styles.priceInput}
    value={value}
    onChange={(e) => {
      if (numericOnly(e.target.value)) onChange(e.target.value);
    }}
    onKeyDown={onKeyDown}
  />
);

const AdPrice = ({
  price,
  priceTo,
  className,
  editable,
  isEditing,
  editValue,
  editToValue,
  onEditStart,
  onEditChange,
  onEditToChange,
  onEditSave,
  onEditCancel,
  adStatus,
  shouldShowDiscountPromoTooltip,
  isPriceChangeTriggeredFromCTA,
  wasDiscountPromoEventTriggered,
  tooltipVisible,
  setTooltipVisible,
  rentPriceType,
  priceType,
  adId,
}: any) => {
  useEffect(() => {
    if (shouldShowDiscountPromoTooltip && !wasDiscountPromoEventTriggered.current) {
      ReactGA.event({
        category: "Snižena cijena",
        action: "Click",
        label: "Prikaz CTA na Moji Oglasi"
      });
      wasDiscountPromoEventTriggered.current = true
    }
  }, [])

  if (!price || price === 0) return null;

  const canEdit = editable && adStatus === AdStatus.active;

  const handleKeyDown = (e: any) => {
    e.stopPropagation();
    if (e.key === "Escape" && onEditCancel) onEditCancel();
    if (e.key === "Enter") onEditSave();
  };


  const handlePriceClick = canEdit ? (e: any) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (onEditStart) onEditStart();
  } : () => { };

  const updateDiscountPromoInLocalStorage = () => {
    const discountPromoInLocalStorage = localStorage.getItem(LocalStorageKey.wasDiscountPromoShown);
    const discountPromoInLocalStorageParsed = discountPromoInLocalStorage ? JSON.parse(discountPromoInLocalStorage) : {}
    const newDiscountPromoInLocalStorage = {
      ...discountPromoInLocalStorageParsed,
      [adId]: Date.now().toString()
    }
    localStorage.setItem(LocalStorageKey.wasDiscountPromoShown, JSON.stringify(newDiscountPromoInLocalStorage));

  }

  const handleTooltipOk = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    setTooltipVisible(false);

    ReactGA.event({
      category: "Snižena cijena",
      action: "Click",
      label: "Klik CTA na Moji Oglasi - ok"
    });

    if (handlePriceClick) handlePriceClick(e);
    isPriceChangeTriggeredFromCTA.current = true
  };

  const handleTooltipCancel = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    setTooltipVisible(false);

    ReactGA.event({
      category: "Snižena cijena",
      action: "Click",
      label: "Klik CTA na Moji Oglasi - odustani"
    });

    updateDiscountPromoInLocalStorage()
  };

  const tooltipContent = (
    <div>
      <p className={styles.tooltipTitle}>Snizite cijenu i povećajte vidljivost oglasa</p>
      <p className={styles.tooltipDescription}>Ovaj oglas ispunjava uvjete za sniženu cijenu. Ako snizite cijenu za više od 5%, oglas će dobiti oznaku “snižena cijena”, dodatnu vidljivost i obavijest korisnicima koji prate oglas.</p>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Button onClick={handleTooltipCancel} style={{ fontSize: 12 }}>Odustani</Button>
        <Button type="primary" style={{ fontSize: 12, fontWeight: "bold", padding: "0 13px" }} onClick={handleTooltipOk}>Snizi cijenu</Button>
      </div>
    </div>
  );


  if (isEditing) {
    return (
      <span className={styles.priceEditContainer}>
        <PriceInput
          value={editValue}
          onChange={onEditChange}
          onKeyDown={handleKeyDown}
          autoFocus
          small={!!priceTo}
        />
        {priceTo ? (
          <React.Fragment>
            <span className={styles.separator}>-</span>
            <PriceInput
              value={editToValue}
              onChange={onEditToChange}
              onKeyDown={handleKeyDown}
              small
            />
          </React.Fragment>
        ) : null}
        <Button
          size="small"
          type="primary"
          shape="circle"
          className={styles.saveButton}
          onClick={onEditSave}
          title="Sačuvaj"
        >
          &#10003;
        </Button>
        <Button
          size="small"
          shape="circle"
          className={styles.cancelButton}
          onClick={onEditCancel}
          title="Odustani"
        >
          &#10005;
        </Button>
      </span>
    );
  }


  return (
    <div>
      <Tooltip
        visible={tooltipVisible}
        title={tooltipContent}
        overlayClassName={styles.tooltip}
      >
        <span
          className={`${styles.price} ${className || ""} ${canEdit ? styles.editable : ""}`}
          onClick={handlePriceClick}
          role={canEdit ? "button" : undefined}
        >
          {displayPrice(price, priceTo, { rentPriceType, priceType })}
          {canEdit && (
            <span className={styles.editIcon}>
              <img src={getIconUrl("edit_icon_red.svg")} />
            </span>
          )}
        </span>
      </Tooltip>
    </div>
  );
};

export default AdPrice;



// WEBPACK FOOTER //
// ./src/containers/MyAdsPage/MyAds/AdSummary/AdPrice/AdPrice.tsx